import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import {
  matchSliceActions,
  selectActiveMatchEvents,
  selectActiveTeam,
  selectActiveTeamOpposite,
  selectActiveTeamPlayerOptions,
  selectActiveTeamPlayerOptionsOpposite,
  selectAllPlayers,
  selectHomeTeam,
  selectMatch,
} from 'features/match/matchSlice';

import { inputSliceActions, selectSelects } from 'features/input/inputSlice';

import AdvancedSelect from 'components/AdvancedSelect';

import { useIntl } from 'react-intl';
import generateSelectPlaceholder from '../../../helpers/generateSelectPlaceholder';
import EventOutcomeButton from '../../EventOutcomeButton';
import { selectTime } from '../../../features/time/timeSlice';
import { MatchEventType } from '../../../enums/matchEventType';
import { TeamOption } from '../../../enums/teamOption';
import publishPenaltyOutcomeEvent from '../../../features/input/thunks/publishPenaltyOutcomeEvent';
import { setActiveSelectValue } from '../../../features/input/thunks';
import selectOnChange from './helpers/selectOnChange';
import { deleteMatchEvent } from '../../../features/match/thunks';
import Team from '../../../interfaces/team';
import { statsSliceActions } from '../../../features/stats/statsSlice';
import findActiveKeeper from '../../../features/input/helpers/findActiveKeeper';
import { MatchStatus } from '../../../enums/matchStatus';
import getPeriodTimeFromMatchLength from '../../MatchWatch/helpers/getPeriodTimeFromMatchLength';
import hasExtraTime from '../../../helpers/hasExtraTime';
import { MatchTime } from '../../../features/time/timeSliceState';
import publishOrUpdateEvent from '../../../features/input/thunks/publishOrUpdateEvent';
import { keyboardActions } from '../../../features/keyboard/keyboardSlice';
import { ActiveFocusItemName } from '../../../features/keyboard/keyboardSliceTypes';
import { useAppDispatch } from '../../../hooks/hooks';

const EventInputPenalty: React.FC = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();

  const match = useSelector(selectMatch);
  const selects = useSelector(selectSelects);
  const playerOptions: any = useSelector(selectActiveTeamPlayerOptions);
  const playerOptionsOpposite: any = useSelector(selectActiveTeamPlayerOptionsOpposite);
  const team: Team | null = useSelector(selectActiveTeam);
  const homeTeam: Team = useSelector(selectHomeTeam);
  const teamOpposite: Team | null = useSelector(selectActiveTeamOpposite);
  const time = useSelector(selectTime);
  const activeMatchEvents = useSelector(selectActiveMatchEvents);
  const allPlayers = useSelector(selectAllPlayers);

  const _hasExtraTime = hasExtraTime(match.matchEvents);

  let penaltySaveEvent;
  let penaltyMissEvent;
  let penaltyEvent;
  activeMatchEvents.forEach((matchEvent) => {
    if (matchEvent.matchEventTypeId === MatchEventType.PENALTY_SAVE) {
      penaltySaveEvent = matchEvent;
    } else if (matchEvent.matchEventTypeId === MatchEventType.PENALTY_MISS) {
      penaltyMissEvent = matchEvent;
    } else if (
      matchEvent.matchEventTypeId === MatchEventType.PENALTY ||
      matchEvent.matchEventTypeId === MatchEventType.PENALTIES_PENALTY
    ) {
      penaltyEvent = matchEvent;
    }
  });

  let penaltyMiss = false;
  let penaltySave = false;
  let penaltyGoal = false;
  if (activeMatchEvents) {
    activeMatchEvents.forEach((matchEvent) => {
      if (
        matchEvent.matchEventTypeId === MatchEventType.PENALTY_MISS ||
        matchEvent.matchEventTypeId === MatchEventType.PENALTIES_MISS
      ) {
        penaltyMiss = true;
      } else if (matchEvent.matchEventTypeId === MatchEventType.PENALTY_SAVE) {
        penaltySave = true;
      } else if (
        matchEvent.matchEventTypeId === MatchEventType.PENALTY_GOAL ||
        matchEvent.matchEventTypeId === MatchEventType.PENALTIES_GOAL
      ) {
        penaltyGoal = true;
      }
    });
  }

  if (!team && !teamOpposite) return null;

  const teamName = team?.shortName ? team.shortName : team?.name;
  const teamNameOpposite = teamOpposite?.shortName ? teamOpposite.shortName : teamOpposite?.name;
  const playerMessage = intl.formatMessage({ id: 'eventInput.player' });

  useEffect(() => {
    if (match.matchStatusId === MatchStatus.PENALTIES) {
      dispatch(inputSliceActions.setActiveSelect(selects.playerPenaltyTaker.id));
    } else {
      dispatch(inputSliceActions.setActiveSelect(selects.playerFouled.id));
    }
  }, []);

  const selectPenaltyMiss = () => {
    if (penaltySave && penaltySaveEvent) {
      dispatch(deleteMatchEvent(penaltySaveEvent));
    } else {
      const personId = selects.playerPenaltyTaker.value;
      dispatch(matchSliceActions.clearActiveMatchEvents());
      dispatch(inputSliceActions.newEvent());
      dispatch(inputSliceActions.setEventTime(getTime(time)));
      dispatch(
        inputSliceActions.setActive({
          team: team?.id === homeTeam.id ? TeamOption.HOME : TeamOption.AWAY,
          eventTypeId:
            match.matchStatusId === MatchStatus.PENALTIES ? MatchEventType.PENALTIES_MISS : MatchEventType.PENALTY_MISS,
        }),
      );
      dispatch(inputSliceActions.setActiveSelect(selects.playerPenaltyTaker.id));
      dispatch(setActiveSelectValue(personId));
      dispatch(
        publishPenaltyOutcomeEvent({
          matchEventTypeId:
            match.matchStatusId === MatchStatus.PENALTIES ? MatchEventType.PENALTIES_MISS : MatchEventType.PENALTY_MISS,
          sorting: penaltyEvent.sorting,
        }),
      );
    }

    //Set choose
    const teamOption = team?.id === match.homeTeam.id ? TeamOption.HOME : TeamOption.AWAY;
    if (match.matchStatusId !== MatchStatus.PENALTIES) {
      dispatch(statsSliceActions.setChoose({ teamOption: teamOption, name: 'postHits' }));
      dispatch(statsSliceActions.setChoose({ teamOption: teamOption, name: 'shotsOffTarget' }));
    }
  };

  const selectPenaltySave = () => {
    if (!penaltyMissEvent) {
      const personId = selects.playerPenaltyTaker.value;
      dispatch(matchSliceActions.clearActiveMatchEvents());
      dispatch(inputSliceActions.newEvent());
      dispatch(inputSliceActions.setEventTime(getTime(time)));
      dispatch(
        inputSliceActions.setActive({
          team: team?.id === homeTeam.id ? TeamOption.HOME : TeamOption.AWAY,
          eventTypeId:
            match.matchStatusId === MatchStatus.PENALTIES ? MatchEventType.PENALTIES_MISS : MatchEventType.PENALTY_MISS,
        }),
      );
      dispatch(inputSliceActions.setActiveSelect(selects.playerPenaltyTaker.id));
      dispatch(setActiveSelectValue(personId));
      dispatch(
        publishPenaltyOutcomeEvent({
          matchEventTypeId:
            match.matchStatusId === MatchStatus.PENALTIES ? MatchEventType.PENALTIES_MISS : MatchEventType.PENALTY_MISS,
          sorting: penaltyEvent.sorting,
        }),
      );
    }
    if (teamOpposite) {
      const person = findActiveKeeper(allPlayers, teamOpposite.id);
      if (person) {
        dispatch(inputSliceActions.setActiveSelect(selects.playerPenaltyKeeper.id));
        dispatch(setActiveSelectValue(person.personId));
      }
      dispatch(
        publishPenaltyOutcomeEvent({ matchEventTypeId: MatchEventType.PENALTY_SAVE, sorting: penaltyEvent.sorting }),
      );
    }
  };

  const getTime = (time: MatchTime): string => {
    let eventTime = time.minutes + ':' + time.seconds.toString().padStart(2, '0');
    if (match.matchStatusId === MatchStatus.PENALTIES) {
      let time;
      if (_hasExtraTime) {
        time = getPeriodTimeFromMatchLength(MatchStatus.PENALTIES, match.matchLength);
      } else {
        time = getPeriodTimeFromMatchLength(MatchStatus.FIRST_HALF_EXTRA_TIME, match.matchLength);
      }
      eventTime = time + ':00';
    }
    return eventTime;
  };

  async function selectPenaltyGoal() {
    dispatch(
      publishPenaltyOutcomeEvent({
        matchEventTypeId:
          match.matchStatusId !== MatchStatus.PENALTIES ? MatchEventType.PENALTY_GOAL : MatchEventType.PENALTIES_GOAL,
        sorting: penaltyEvent.sorting,
      }),
    );

    if (match.matchStatusId !== MatchStatus.PENALTIES) {
      dispatch(matchSliceActions.clearActiveMatchEvents());
      dispatch(inputSliceActions.newEvent());
      dispatch(inputSliceActions.setEventTime(time.minutes + ':' + time.seconds.toString().padStart(2, '0')));
      dispatch(
        inputSliceActions.setActive({
          team: team?.id === homeTeam.id ? TeamOption.HOME : TeamOption.AWAY,
          eventTypeId: MatchEventType.GOAL,
        }),
      );

      if (selects.playerPenaltyTaker.value !== selects.playerFouled.value && selects.playerFouled.value) {
        dispatch(inputSliceActions.setActiveSelect(selects.assist.id));
        dispatch(setActiveSelectValue(selects.playerFouled.value));
        dispatch(inputSliceActions.setIndirectAssistCheckbox(true));
      }

      dispatch(inputSliceActions.setActiveSelect(selects.goalScorer.id));
      if (penaltyEvent.person) {
        dispatch(setActiveSelectValue(penaltyEvent.person.id));
      }
      await dispatch(publishOrUpdateEvent({}));

      if (selects.playerPenaltyTaker.value !== selects.playerFouled.value && selects.playerFouled.value) {
        dispatch(publishPenaltyOutcomeEvent({ matchEventTypeId: MatchEventType.INDIRECT_ASSIST }));
      }
    } else {
      const personId = selects.playerPenaltyTaker.value;
      dispatch(matchSliceActions.clearActiveMatchEvents());
      dispatch(inputSliceActions.newEvent());
      dispatch(inputSliceActions.setEventTime(getTime(time)));
      dispatch(inputSliceActions.setSelectPlayerPenaltyTaker(personId));
      dispatch(inputSliceActions.setActiveSelect(null));
      dispatch(
        inputSliceActions.setActive({
          team: team?.id === homeTeam.id ? TeamOption.HOME : TeamOption.AWAY,
          eventTypeId: MatchEventType.PENALTIES_GOAL,
        }),
      );
      dispatch(keyboardActions.setActiveFocusItem(ActiveFocusItemName.COMMENT));
    }
  }

  const selectGoalOnRebound = () => {
    dispatch(
      publishPenaltyOutcomeEvent({ matchEventTypeId: MatchEventType.PENALTY_MISS, sorting: penaltyEvent.sorting }),
    );

    dispatch(matchSliceActions.clearActiveMatchEvents());
    dispatch(inputSliceActions.newEvent());
    dispatch(inputSliceActions.setEventTime(time.minutes + ':' + time.seconds.toString().padStart(2, '0')));
    dispatch(
      inputSliceActions.setActive({
        team: team?.id === homeTeam.id ? TeamOption.HOME : TeamOption.AWAY,
        eventTypeId: MatchEventType.GOAL,
      }),
    );
    dispatch(publishOrUpdateEvent({}));
  };

  const onChangePlayerFouled = (option) => {
    selectOnChange(option, team, dispatch, intl);
    dispatch(inputSliceActions.setActiveSelect(selects.playerOffence.id));
  };

  const onChangePlayerOffence = (option) => {
    selectOnChange(option, teamOpposite, dispatch, intl);
    dispatch(inputSliceActions.setActiveSelect(selects.playerPenaltyTaker.id));
  };

  const onChangePenaltyTaker = (option) => {
    selectOnChange(option, team, dispatch, intl);
  };

  let className = 'inputsLine';
  let className2 = 'buttonsLine';
  if (match.matchStatusId === MatchStatus.PENALTIES) {
    className += ' inputLinePenalties';
    className2 += ' buttonsLinePenalties';
  }

  return (
    <div className="input-group penalty">
      <div className={className}>
        {match.matchStatusId !== MatchStatus.PENALTIES && (
          <AdvancedSelect
            title={intl.formatMessage({ id: 'selectHeader.playerFouled' })}
            placeholder={generateSelectPlaceholder(intl, playerMessage, teamName)}
            options={playerOptions}
            select={selects.playerFouled}
            onChange={onChangePlayerFouled}
            isClearable={true}
          />
        )}
        {match.matchStatusId !== MatchStatus.PENALTIES && (
          <AdvancedSelect
            title={intl.formatMessage({ id: 'selectHeader.playerOffence' })}
            placeholder={generateSelectPlaceholder(intl, playerMessage, teamNameOpposite)}
            options={playerOptionsOpposite}
            select={selects.playerOffence}
            onChange={onChangePlayerOffence}
            isClearable={true}
          />
        )}
        <AdvancedSelect
          title={intl.formatMessage({ id: 'selectHeader.playerPenaltyTaker' })}
          placeholder={generateSelectPlaceholder(intl, playerMessage, teamName)}
          options={playerOptions}
          select={selects.playerPenaltyTaker}
          onChange={onChangePenaltyTaker}
          isClearable={true}
        />
      </div>
      {activeMatchEvents && activeMatchEvents.length > 0 && (
        <div className={className2}>
          <div>
            {!penaltyGoal && (
              <EventOutcomeButton
                key={MatchEventType.PENALTY_MISS}
                matchEventTypeId={MatchEventType.PENALTY_MISS}
                title={intl.formatMessage({ id: 'events.11' })}
                onClickCallback={selectPenaltyMiss}
                selected={penaltyMiss && !penaltySave}
              />
            )}
          </div>
          <div>
            {!penaltyGoal && (
              <EventOutcomeButton
                key={MatchEventType.PENALTY_SAVE}
                matchEventTypeId={MatchEventType.PENALTY_SAVE}
                title={intl.formatMessage({ id: 'events.130' })}
                onClickCallback={selectPenaltySave}
                selected={penaltySave}
              />
            )}
          </div>
          <div>
            {!penaltyMiss && !penaltySave && (
              <EventOutcomeButton
                key={MatchEventType.PENALTY_GOAL}
                matchEventTypeId={MatchEventType.PENALTY_GOAL}
                title={intl.formatMessage({ id: 'events.10' })}
                onClickCallback={selectPenaltyGoal}
                disabled={penaltyGoal}
                selected={penaltyGoal}
              />
            )}
          </div>
          <div>
            {match.matchStatusId !== MatchStatus.PENALTIES && !penaltyMiss && !penaltySave && !penaltyGoal && (
              <EventOutcomeButton
                key={MatchEventType.GOAL}
                matchEventTypeId={MatchEventType.GOAL}
                title={intl.formatMessage({ id: 'events.goalOnRebound' })}
                onClickCallback={selectGoalOnRebound}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventInputPenalty;
